'use client';

import { API_STATUS } from '@/contants/api';
import CircleLoading from '@/components/loading/circle-loading';
import { useCopilotAccess } from '@/hooks/use-copilot-access';
import { getCurrentUserSession, getUserInfoFromIdP } from '@/services/auth';
import { fetchUserWorkspaces } from '@/services/workspace';
import { useGlobalStore } from '@/store/global';
import { systemStatusSelectors } from '@/store/global/selectors';
import { useUserStore } from '@/store/user/store';
import { useWorkspaceStore } from '@/store/workspace';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';

// Allow disabling auth redirects/checks in non-prod contexts (e.g. Playwright).
// NOTE: Playwright config sets NEXT_PUBLIC_ENABLE_AUTH_PROTECTION=0.
// Additionally, we auto-disable auth when running under Playwright to keep smoke
// tests stable even if env injection is missed by the dev server / bundler.
const AUTH_PROTECTION_ENABLED =
  process.env.NEXT_PUBLIC_ENABLE_AUTH_PROTECTION !== '0' &&
  !(typeof navigator !== 'undefined' && /playwright/i.test(navigator.userAgent));

interface AuthProviderProps extends PropsWithChildren {
  /**
   * Paths that should skip automatic authorization
   * @default ['/login', '/signup', '/auth', '/callback', '/error', '/403', '/unauthorized']
   */
  skipPaths?: string[];
}

const DEFAULT_SKIP_PATHS = [
  '/login',
  '/signup',
  '/auth',
  '/callback',
  '/login-callback',
  '/error',
  '/403',
  '/unauthorized',
];

// Module-level flag so it survives React Strict Mode's unmount/remount cycle
// in development.  A useRef would be reset to `false` on the second mount.
let __authCheckStarted = false;

/**
 * AuthProvider component that automatically initiates SSO authorization
 * when the app loads and the user is not authenticated
 */
export const AuthProvider: FC<AuthProviderProps> = ({
  children,
  skipPaths = DEFAULT_SKIP_PATHS,
}) => {
  const [isChecking, setIsChecking] = useState(AUTH_PROTECTION_ENABLED);
  const { setUser, setAuthenticated, setInitialized, authorize } = useUserStore();
  const pathname = usePathname();
  const workspacesLoaded = useWorkspaceStore(s => s.workspacesLoaded);
  const hasWorkspaces = useWorkspaceStore(s => s.workspaces.length > 0);
  const access = useCopilotAccess();

  useEffect(() => {
    // Allow the app to render without triggering auth requests/redirects.
    if (!AUTH_PROTECTION_ENABLED) return;

    const checkAuthAndInitiate = async () => {
      // Skip if user is currently logging out
      if (useUserStore.getState().isLoggingOut) {
        console.log('[AuthProvider] User is logging out, skipping auth check');
        setIsChecking(false);
        return;
      }

      // Prevent multiple simultaneous checks (survives Strict Mode remount)
      if (__authCheckStarted) {
        setIsChecking(false);
        return;
      }

      // Mark as checked immediately to prevent re-entry when store updates
      // trigger re-renders and the effect re-fires before the async work completes.
      __authCheckStarted = true;

      // Skip if on excluded paths
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const shouldSkip = skipPaths.some(
          path => currentPath.startsWith(path) || currentPath.includes(path)
        );

        if (shouldSkip) {
          console.log('[AuthProvider] Skipping auth check for path:', currentPath);
          setIsChecking(false);
          return;
        }

        // Check if we're returning from an OAuth callback
        const urlParams = new URLSearchParams(window.location.search);
        if (
          urlParams.has('code') ||
          urlParams.has('state') ||
          currentPath.includes('login-callback')
        ) {
          console.log('[AuthProvider] Skipping auth check - OAuth callback detected');
          setIsChecking(false);
          return;
        }
      }

      try {
        // Check again if logging out (state might have changed)
        if (useUserStore.getState().isLoggingOut) {
          console.log('[AuthProvider] User started logging out, aborting auth check');
          setIsChecking(false);
          return;
        }

        // Step 1: Fast session check from cached ID token
        const userSessionResponse = await getCurrentUserSession();

        if (userSessionResponse.status == API_STATUS.OK && userSessionResponse.data) {
          // Set user info state when session response is OK
          const userData = userSessionResponse.data[0] || null;
          if (userData) {
            // Convert the user data to the format expected by auth store
            setUser(userData as any); // Type conversion needed due to different User types
            setAuthenticated(true);
            setInitialized(true);
          }

          // Step 2: Validate with the IdP and finish any token refresh before
          // another session-backed request can consume the rotating refresh token.
          await getUserInfoFromIdP();

          // Fetch user workspaces after successful session validation
          // IMPORTANT: Wait for workspace fetch to complete before rendering children
          try {
            const workspaces = await fetchUserWorkspaces();

            // Store workspaces in Zustand store (will auto-select first if none selected)
            useWorkspaceStore.getState().setWorkspaces(workspaces);
          } catch (workspaceError) {
            console.error('[AuthProvider] Failed to fetch workspaces:', workspaceError);
            // Don't fail authentication if workspace fetch fails
            // But still mark as loaded so app can render (just without workspace context)
          }

          // Only allow children to render AFTER workspace fetch completes
          setIsChecking(false);
          return;
        }

        // Session check failed or returned non-OK status
        // Check if user is logging out before redirecting
        if (useUserStore.getState().isLoggingOut) {
          console.log('[AuthProvider] User is logging out, skipping redirect after session fail');
          setIsChecking(false);
          return;
        }

        // Clear any stale user state and redirect to login
        console.warn(
          '[AuthProvider] Session validation failed, clearing user state and redirecting to login'
        );
        setUser(undefined);
        setAuthenticated(false);
        setInitialized(false);

        // Initiate SSO authorization (this will redirect the browser)
        await authorize();
      } catch (error) {
        console.error('[AuthProvider] Error during auth check/initiation:', error);

        // Check if user is logging out before redirecting
        if (useUserStore.getState().isLoggingOut) {
          console.log('[AuthProvider] User is logging out, skipping redirect after error');
          setIsChecking(false);
          return;
        }

        // Clear user state on error
        setUser(undefined);
        setAuthenticated(false);
        setInitialized(false);

        // Try to redirect to login
        try {
          await authorize();
        } catch (authError) {
          console.error('[AuthProvider] Failed to initiate authorization:', authError);
        }

        setIsChecking(false);
      }
    };

    // Only run once on mount
    checkAuthAndInitiate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showGlobalLoading = useGlobalStore(systemStatusSelectors.showGlobalLoading);
  const isChatRoute = pathname === '/' || pathname === '/chat' || pathname?.startsWith('/chat/');
  const isResolvingAccess = isChatRoute && (!workspacesLoaded || (hasWorkspaces && !access.isReady));
  const isLoading = isChecking || isResolvingAccess;

  return (
    <>
      {showGlobalLoading && isLoading && <CircleLoading fullScreen />}
      {/* Mount workspace resolution after auth; its access boundary still gates protected content. */}
      {!isChecking && children}
    </>
  );
};

export default AuthProvider;
